import { Loader } from '@googlemaps/js-api-loader';
/**
 * Wrapper class for Google Maps API. 
 * (Requires GoogleMaps script)
 * 
 * See: https://developers.google.com/maps/documentation/javascript/tutorial
 * 
 * @property {string} apiKey            Holds the API key to use GoogleMaps.
 * @property {string} type              Init mode of map, to initialize specific functions to each use.
 */

export class GoogleMaps {
  static create(options) {
    return new GoogleMaps(options);
  }

  constructor(options = {}) {
    const {
      apiKey,
      type = 'embed'
    } = options;

    if (!apiKey) {
      throw new Error('Google Maps API Key is required to use this class');
    }

    this.loaded = false;
    this.type = type;
    this.loader = new Loader({
      apiKey,
      version: 'weekly',
      libraries: ['places']
    });

    switch (this.type) {
      case 'distance':
        {
          this.loadMapsAPI();
          break;
        }

      case 'embed':
      default:
        {
          console.error('Embed Maps is not implemented yet.');
          break;
        }
    }
  }
  /**
   * Loads GoogleMaps instance with Geocoder and DirectionsService.
   * @returns {Promise} Returns Google, Geocoder and DirectionsService libraries loaded. 
   */


  async loadMapsAPI() {
    if (this.loaded) {
      return;
    }

    const google = await this.loader.load();
    this.google = google;
    this.geocoder = new google.maps.Geocoder();
    this.directionsService = new google.maps.DirectionsService();
    this.loaded = true;
  }
  /**
   * Calculates distance between two points.
   * @returns {string} Distance (walking, driving, transit or bicycling) 
   *                   between start location and end location in kilometers.
   */


  async getDistance(postalCode, endLocation) {
    if (!this.loaded) {
      await this.loadMapsAPI();
    }

    try {
      const startLocation = await this.convertPostalCodeToLocation(postalCode);
      return this.getDistanceFromStartToEnd(startLocation, endLocation);
    } catch (e) {
      throw new Error(`Could not get Distance: ${distance}`);
    }
  }
  /**
   * Converts from Postal Code string to Latitude and Longitude array
   */


  async convertPostalCodeToLocation(postalCode) {
    try {
      const geocodeResponse = await this.geocoder.geocode({
        address: postalCode
      });
      return {
        lng: geocodeResponse.results[0].geometry.location.lng(),
        lat: geocodeResponse.results[0].geometry.location.lat()
      };
    } catch (e) {
      throw new Error(`Could not use Geocode Service: ${e}`);
    }
  }
  /**
   * Gets the distance from startLocation to endLocation (walking, driving, 
   * transit or bicycling) using Google Direction Service
   * 
   * @param {object} startLocation        Holds the start coordinates to calculate distantce from
   * @param {number} startLocation.lat    Start point Latitude
   * @param {number} startLocation.lng    Start point Longitude
   * @param {object} endLocation          Holds the end coordinates to calculate distance to
   * @param {number} endLocation.lat      Start point Latitude
   * @param {number} endLocation.lng      Start point Longitude
   */


  async getDistanceFromStartToEnd(startLocation, endLocation, travelMode = 'WALKING') {
    try {
      const routeResponse = await this.directionsService.route({
        origin: startLocation,
        destination: endLocation,
        travelMode
      });
      return routeResponse.routes[0].legs[0].distance.text;
    } catch (e) {
      throw new Error(`Could not use Direction Service: ${e}`);
    }
  }

}
//# sourceMappingURL=GoogleMaps.js.map